import type { Html, HtmlBuilder } from "foldkit/html";

import type { AgentBlock, Provenance } from "../api/schemas/common";
import type { EntryAuthor } from "../api/schemas/entries";
import { i18next } from "../i18n";

/**
 * Who wrote something (KTD8, KTD9): a person as before, an "Agent (Sam)"
 * label with an info popover for agent channels, or a bare channel badge
 * when a version predates provenance. The label never depends on the
 * self-reported or handshake strings; those only fill the popover.
 */

type Author = typeof EntryAuthor.Type;

const AGENT_CHANNELS: ReadonlySet<string> = new Set([
	"mcp",
	"chat",
	"telegram",
]);

const SOURCE_BADGE_CLASSES: Readonly<Record<string, string>> = {
	api: "badge-primary",
	mcp: "badge-secondary",
	chat: "badge-accent",
	telegram: "badge-accent",
	backfill: "badge-ghost",
};

export const sourceBadgeClass = (source: string): string =>
	SOURCE_BADGE_CLASSES[source] ?? "badge-neutral";

export type ProvenanceLabel =
	| { kind: "person"; name: string; imageUrl: string | null }
	| {
			kind: "agent";
			channel: string;
			person: string | null;
			agent: AgentBlock | null;
	  }
	| { kind: "channel"; channel: string }
	| { kind: "none" };

/**
 * Agent channels with a person or an agent block show "Agent (<person>)";
 * agent channels with neither (pre-feature MCP writes) show the channel
 * badge only; every other channel shows the person when known. With no
 * provenance at all (an older response) the entry's author stands in.
 */
export const provenanceLabel = (
	provenance: Provenance | null | undefined,
	author?: Author | null,
): ProvenanceLabel => {
	if (!provenance) {
		return author?.name
			? { kind: "person", name: author.name, imageUrl: author.imageUrl }
			: { kind: "none" };
	}
	const { channel, person, agent } = provenance;
	if (AGENT_CHANNELS.has(channel)) {
		return person || agent
			? { kind: "agent", channel, person: person?.name ?? null, agent }
			: { kind: "channel", channel };
	}
	return person?.name
		? { kind: "person", name: person.name, imageUrl: person.imageUrl }
		: { kind: "none" };
};

export const agentLabelText = (person: string | null): string =>
	person
		? i18next.t("provenance.agentLabel", { name: person })
		: i18next.t("provenance.agent");

const channelText = (channel: string): string =>
	i18next.t(`provenance.channels.${channel}`, { defaultValue: channel });

const grantText = (grant: NonNullable<AgentBlock["grant"]>): string => {
	const kind = i18next.t(`provenance.grants.${grant.kind}`, {
		defaultValue: grant.kind,
	});
	return grant.label ? `${kind}: ${grant.label}` : kind;
};

/** The popover rows (R18): missing fields are omitted, claims are marked. */
export const provenanceRows = (
	channel: string,
	agent: AgentBlock | null,
): ReadonlyArray<{ label: string; value: string; note?: string }> => {
	const t = (key: string) => i18next.t(`provenance.${key}`);
	const rows: { label: string; value: string; note?: string }[] = [
		{ label: t("channel"), value: channelText(channel) },
	];
	if (agent?.tool) {
		rows.push({
			label: t("tool"),
			value: agent.tool.version
				? `${agent.tool.name} ${agent.tool.version}`
				: agent.tool.name,
			note: t("reportedByClient"),
		});
	}
	if (agent?.grant)
		rows.push({ label: t("grant"), value: grantText(agent.grant) });
	const claim =
		agent?.reported === "agent"
			? { note: t("reportedByAgent") }
			: agent?.reported === "server"
				? { note: t("knownToServer") }
				: {};
	if (agent?.vendor)
		rows.push({ label: t("vendor"), value: agent.vendor, ...claim });
	if (agent?.model)
		rows.push({ label: t("model"), value: agent.model, ...claim });
	return rows;
};

const personView = <M>(
	h: HtmlBuilder<M>,
	name: string,
	imageUrl: string | null,
): Html =>
	h.div(
		[h.Class("flex items-center gap-1.5")],
		[
			imageUrl
				? h.img([h.Src(imageUrl), h.Alt(name), h.Class("h-4 w-4 rounded-full")])
				: h.div(
						[
							h.Class(
								"flex h-4 w-4 items-center justify-center rounded-full bg-primary/20 text-[9px] font-medium text-primary",
							),
						],
						[name.charAt(0).toUpperCase()],
					),
			h.span([], [name]),
		],
	);

const channelBadge = <M>(h: HtmlBuilder<M>, channel: string): Html =>
	h.span(
		[h.Class(`badge badge-sm ${sourceBadgeClass(channel)}`)],
		[channelText(channel)],
	);

/** The focus dropdown `src/app/timeTravel.ts` uses: no open state, closes on blur. */
const agentView = <M>(
	h: HtmlBuilder<M>,
	label: Extract<ProvenanceLabel, { kind: "agent" }>,
): Html =>
	h.div(
		[h.Class("dropdown dropdown-end inline-flex items-center gap-1")],
		[
			h.span(
				[h.Class("font-medium text-base-content/80")],
				[agentLabelText(label.person)],
			),
			h.button(
				[
					h.Type("button"),
					h.Tabindex(0),
					h.Class("btn btn-ghost btn-circle btn-xs"),
					h.AriaLabel(i18next.t("provenance.details")),
				],
				[h.span([h.Class("text-xs font-semibold")], ["i"])],
			),
			h.div(
				[
					h.Tabindex(0),
					h.Class(
						"dropdown-content z-50 mt-1 w-72 rounded-box border border-base-300 bg-base-100 p-3 text-xs shadow-lg",
					),
				],
				[
					h.dl(
						[h.Class("grid grid-cols-[auto_1fr] gap-x-3 gap-y-1")],
						provenanceRows(label.channel, label.agent).flatMap((row) => [
							h.dt(
								[h.Key(`${row.label}-dt`), h.Class("text-base-content/60")],
								[row.label],
							),
							h.dd(
								[h.Key(`${row.label}-dd`), h.Class("text-base-content")],
								[
									h.span([], [row.value]),
									row.note
										? h.span(
												[h.Class("ml-1 text-base-content/50")],
												[`(${row.note})`],
											)
										: h.empty,
								],
							),
						]),
					),
				],
			),
		],
	);

type ViewOptions = {
	/** Off where the caller already shows a channel badge (the history panels). */
	channelBadge?: boolean;
};

const renderLabel = <M>(
	h: HtmlBuilder<M>,
	label: ProvenanceLabel,
	options: ViewOptions = {},
): Html => {
	switch (label.kind) {
		case "person":
			return personView(h, label.name, label.imageUrl);
		case "agent":
			return agentView(h, label);
		case "channel":
			return options.channelBadge === false
				? h.empty
				: channelBadge(h, label.channel);
		case "none":
			return h.empty;
	}
};

/** Renders the label for a write; `h.empty` when there is nothing to show. */
export const provenanceView = <M>(
	h: HtmlBuilder<M>,
	provenance: Provenance | null | undefined,
	author?: Author | null,
	options?: ViewOptions,
): Html => renderLabel(h, provenanceLabel(provenance, author), options);

/** "Last edited by …" for page headers (R17); `h.empty` without a last edit. */
export const lastEditView = <M>(
	h: HtmlBuilder<M>,
	lastEdit: Provenance | null | undefined,
): Html => {
	const label = provenanceLabel(lastEdit);
	if (label.kind === "none") return h.empty;
	return h.div(
		[
			h.Class("flex items-center gap-1.5 text-xs text-base-content/60"),
			h.DataAttribute("testid", "last-edit"),
		],
		[h.span([], [i18next.t("provenance.lastEditedBy")]), renderLabel(h, label)],
	);
};
